import { createStore, type StoreApi } from "zustand";
import { getTurnstile } from "@/utils/cloudflare/turnstile";

export class InputCaptchaElement extends HTMLElement {
  private error: HTMLElement;
  private container!: HTMLElement;
  private turnstile?: Turnstile.Turnstile;
  private sitekey: string = "";
  private state: StoreApi<{
    error: Error | undefined;
    token: string;
    isValidating: boolean;
  }>;

  constructor() {
    super();

    this.error = document.createElement("div");
    this.error.classList.add("input-error");

    this.state = createStore(() => ({
      error: undefined,
      token: "",
      isValidating: false,
    }));

    this.state.subscribe((state, prevState) => {
      if (state.isValidating !== prevState.isValidating) {
        this.dispatchEvent(
          new CustomEvent("captcha:onValidating", {
            detail: state.isValidating,
          }),
        );
      }

      if (state.token !== prevState.token) {
        this.dispatchEvent(
          new CustomEvent("captcha:onToken", {
            detail: state.token,
          }),
        );
      }

      if (state.error !== prevState.error) {
        if (state.error !== undefined) {
          this.error.replaceChildren(new Text(state.error.message));

          this.appendChild(this.error);

          this.classList.add("input-invalid");
        } else {
          this.classList.remove("input-invalid");

          this.error?.remove();
        }
      }
    });
  }

  connectedCallback() {
    this.container = this.querySelector<HTMLElement>("[data-sitekey]")!;
    this.sitekey = this.container.getAttribute("data-sitekey") ?? "";

    this.render();
  }

  private async render() {
    this.state.setState({
      isValidating: true,
    });

    this.turnstile = await getTurnstile();

    this.turnstile.render(this.container, {
      sitekey: this.sitekey,

      callback: (token) => {
        this.state.setState({
          error: undefined,
          token,
          isValidating: false,
        });
      },

      "error-callback": () => {
        this.state.setState({
          isValidating: false,
        });
      },

      "expired-callback": () => {
        this.reset();
      },

      "before-interactive-callback": () => {
        this.state.setState({
          error: undefined,
          isValidating: true,
        });
      },

      "after-interactive-callback": () => {},
    });
  }

  setError(error: Error | undefined) {
    this.state.setState({
      error,
    });
  }

  getName() {
    return (
      this.querySelector(".cf-turnstile")?.getAttribute(
        "data-response-field-name",
      ) ?? ""
    );
  }

  reset() {
    this.state.setState({
      isValidating: true,
    });
    this.turnstile?.reset(this.container);
  }

  getState() {
    return this.state.getState();
  }
}
